import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  Chip,
  Avatar,
  IconButton,
  useTheme,
  Grid,
} from '@mui/material';
import {
  Person as PersonIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

interface PersonaData {
  demographics: {
    ageGroup: string;
    gender: string;
    education: string;
    occupation: string;
    location: string;
    income: string;
  };
  political: {
    partyAffiliation: string;
    politicalLean: string;
    votingFrequency: string;
    issuesPriority: string[];
  };
  media: {
    newsSources: string[];
    socialMediaUsage: string;
    trustLevel: number;
  };
  voting: {
    decisionFactors: string[];
    candidateQualities: string[];
    votingMethod: string;
  };
}

interface PersonaBuilderProps {
  onComplete: (persona: PersonaData) => void;
  onCancel: () => void;
}

const PersonaBuilder: React.FC<PersonaBuilderProps> = ({ onComplete, onCancel }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [persona, setPersona] = useState<PersonaData>({
    demographics: {
      ageGroup: '',
      gender: '',
      education: '',
      occupation: '',
      location: '',
      income: '',
    },
    political: {
      partyAffiliation: '',
      politicalLean: '',
      votingFrequency: '',
      issuesPriority: [],
    },
    media: {
      newsSources: [],
      socialMediaUsage: '',
      trustLevel: 5,
    },
    voting: {
      decisionFactors: [],
      candidateQualities: [],
      votingMethod: '',
    },
  });

  const steps = [
    'Demographics',
    'Political Views',
    'Media Habits',
    'Voting Preferences',
  ];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleComplete = () => {
    onComplete(persona);
  };

  const updatePersona = (category: keyof PersonaData, field: string, value: any) => {
    setPersona(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const handleArrayToggle = (category: keyof PersonaData, field: string, value: string) => {
    setPersona(prev => {
      const currentArray = (prev[category] as any)[field] as string[];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [field]: newArray,
        },
      };
    });
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Tell us about yourself (anonymously)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This helps us understand voter demographics without collecting personal information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Age Group</InputLabel>
                  <Select
                    value={persona.demographics.ageGroup}
                    label="Age Group"
                    onChange={(e) => updatePersona('demographics', 'ageGroup', e.target.value)}
                  >
                    <MenuItem value="18-24">18-24</MenuItem>
                    <MenuItem value="25-34">25-34</MenuItem>
                    <MenuItem value="35-44">35-44</MenuItem>
                    <MenuItem value="45-54">45-54</MenuItem>
                    <MenuItem value="55-64">55-64</MenuItem>
                    <MenuItem value="65+">65+</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={persona.demographics.gender}
                    label="Gender"
                    onChange={(e) => updatePersona('demographics', 'gender', e.target.value)}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                    <MenuItem value="prefer-not">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Education Level</InputLabel>
                  <Select
                    value={persona.demographics.education}
                    label="Education Level"
                    onChange={(e) => updatePersona('demographics', 'education', e.target.value)}
                  >
                    <MenuItem value="high-school">High School</MenuItem>
                    <MenuItem value="some-college">Some College</MenuItem>
                    <MenuItem value="bachelor">Bachelor's Degree</MenuItem>
                    <MenuItem value="master">Master's Degree</MenuItem>
                    <MenuItem value="phd">PhD/Doctorate</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Occupation</InputLabel>
                  <Select
                    value={persona.demographics.occupation}
                    label="Occupation"
                    onChange={(e) => updatePersona('demographics', 'occupation', e.target.value)}
                  >
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="employed">Employed</MenuItem>
                    <MenuItem value="self-employed">Self-Employed</MenuItem>
                    <MenuItem value="business-owner">Business Owner</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="unemployed">Unemployed</MenuItem>
                    <MenuItem value="homemaker">Homemaker</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={persona.demographics.location}
                    label="Location"
                    onChange={(e) => updatePersona('demographics', 'location', e.target.value)}
                  >
                    <MenuItem value="lagos">Lagos</MenuItem>
                    <MenuItem value="abuja">Abuja</MenuItem>
                    <MenuItem value="kano">Kano</MenuItem>
                    <MenuItem value="ibadan">Ibadan</MenuItem>
                    <MenuItem value="kaduna">Kaduna</MenuItem>
                    <MenuItem value="port-harcourt">Port Harcourt</MenuItem>
                    <MenuItem value="benin-city">Benin City</MenuItem>
                    <MenuItem value="maiduguri">Maiduguri</MenuItem>
                    <MenuItem value="zaria">Zaria</MenuItem>
                    <MenuItem value="aba">Aba</MenuItem>
                    <MenuItem value="jos">Jos</MenuItem>
                    <MenuItem value="ilorin">Ilorin</MenuItem>
                    <MenuItem value="enugu">Enugu</MenuItem>
                    <MenuItem value="funtua">Funtua</MenuItem>
                    <MenuItem value="makurdi">Makurdi</MenuItem>
                    <MenuItem value="sokoto">Sokoto</MenuItem>
                    <MenuItem value="onitsha">Onitsha</MenuItem>
                    <MenuItem value="warri">Warri</MenuItem>
                    <MenuItem value="calabar">Calabar</MenuItem>
                    <MenuItem value="akure">Akure</MenuItem>
                    <MenuItem value="uyo">Uyo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Income Range</InputLabel>
                  <Select
                    value={persona.demographics.income}
                    label="Income Range"
                    onChange={(e) => updatePersona('demographics', 'income', e.target.value)}
                  >
                    <MenuItem value="below-100k">Below ₦100,000</MenuItem>
                    <MenuItem value="100k-250k">₦100,000 - ₦250,000</MenuItem>
                    <MenuItem value="250k-500k">₦250,000 - ₦500,000</MenuItem>
                    <MenuItem value="500k-1m">₦500,000 - ₦1,000,000</MenuItem>
                    <MenuItem value="1m-2m">₦1,000,000 - ₦2,000,000</MenuItem>
                    <MenuItem value="2m-5m">₦2,000,000 - ₦5,000,000</MenuItem>
                    <MenuItem value="above-5m">Above ₦5,000,000</MenuItem>
                    <MenuItem value="prefer-not">Prefer not to say</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Your Political Views
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Help us understand your political preferences
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Party Affiliation</InputLabel>
                  <Select
                    value={persona.political.partyAffiliation}
                    label="Party Affiliation"
                    onChange={(e) => updatePersona('political', 'partyAffiliation', e.target.value)}
                  >
                    <MenuItem value="apc">APC - All Progressives Congress</MenuItem>
                    <MenuItem value="pdp">PDP - People's Democratic Party</MenuItem>
                    <MenuItem value="lp">LP - Labour Party</MenuItem>
                    <MenuItem value="nnpp">NNPP - New Nigeria Peoples Party</MenuItem>
                    <MenuItem value="apga">APGA - All Progressives Grand Alliance</MenuItem>
                    <MenuItem value="sdp">SDP - Social Democratic Party</MenuItem>
                    <MenuItem value="independent">Independent</MenuItem>
                    <MenuItem value="undecided">Undecided</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Political Lean
                  </Typography>
                  <RadioGroup
                    value={persona.political.politicalLean}
                    onChange={(e) => updatePersona('political', 'politicalLean', e.target.value)}
                  >
                    <FormControlLabel value="very-conservative" control={<Radio />} label="Very Conservative" />
                    <FormControlLabel value="conservative" control={<Radio />} label="Conservative" />
                    <FormControlLabel value="moderate" control={<Radio />} label="Moderate" />
                    <FormControlLabel value="liberal" control={<Radio />} label="Liberal" />
                    <FormControlLabel value="very-liberal" control={<Radio />} label="Very Liberal" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Voting Frequency</InputLabel>
                  <Select
                    value={persona.political.votingFrequency}
                    label="Voting Frequency"
                    onChange={(e) => updatePersona('political', 'votingFrequency', e.target.value)}
                  >
                    <MenuItem value="always">Always vote in elections</MenuItem>
                    <MenuItem value="often">Vote in most elections</MenuItem>
                    <MenuItem value="sometimes">Vote sometimes</MenuItem>
                    <MenuItem value="rarely">Rarely vote</MenuItem>
                    <MenuItem value="first-time">First-time voter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  What issues matter most to you? (Select up to 3)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    'Economy',
                    'Healthcare',
                    'Education',
                    'Security',
                    'Infrastructure',
                    'Corruption',
                    'Agriculture',
                    'Technology',
                    'Environment',
                    'Social Justice',
                  ].map((issue) => (
                    <Chip
                      key={issue}
                      label={issue}
                      clickable
                      color={persona.political.issuesPriority.includes(issue) ? 'primary' : 'default'}
                      onClick={() => handleArrayToggle('political', 'issuesPriority', issue)}
                      disabled={persona.political.issuesPriority.length >= 3 && !persona.political.issuesPriority.includes(issue)}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Media & Information Habits
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              How do you consume news and information?
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Primary News Sources (Select up to 3)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {[
                    'TV News',
                    'Newspapers',
                    'Radio',
                    'Social Media',
                    'News Websites',
                    'Word of Mouth',
                    'Government Sources',
                    'International Media',
                  ].map((source) => (
                    <Chip
                      key={source}
                      label={source}
                      clickable
                      color={persona.media.newsSources.includes(source) ? 'primary' : 'default'}
                      onClick={() => handleArrayToggle('media', 'newsSources', source)}
                      disabled={persona.media.newsSources.length >= 3 && !persona.media.newsSources.includes(source)}
                    />
                  ))}
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Social Media Usage</InputLabel>
                  <Select
                    value={persona.media.socialMediaUsage}
                    label="Social Media Usage"
                    onChange={(e) => updatePersona('media', 'socialMediaUsage', e.target.value)}
                  >
                    <MenuItem value="heavy">Heavy - Multiple times daily</MenuItem>
                    <MenuItem value="moderate">Moderate - Once daily</MenuItem>
                    <MenuItem value="light">Light - Few times weekly</MenuItem>
                    <MenuItem value="rare">Rarely</MenuItem>
                    <MenuItem value="never">Never</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }} sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Trust in Media Sources
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  How much do you trust the news sources you follow?
                </Typography>
                <Box sx={{ px: 1 }}>
                  <Slider
                    value={persona.media.trustLevel}
                    onChange={(_e, value) => updatePersona('media', 'trustLevel', value)}
                    min={1}
                    max={10}
                    marks={[
                      { value: 1, label: 'Very Low' },
                      { value: 5, label: 'Neutral' },
                      { value: 10, label: 'Very High' },
                    ]}
                    valueLabelDisplay="auto"
                    sx={{ mb: 2 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Voting Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              What influences your voting decisions?
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Key Decision Factors (Select up to 3)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {[
                    'Party Affiliation',
                    'Candidate Character',
                    'Economic Policies',
                    'Past Performance',
                    'Campaign Promises',
                    'Endorsements',
                    'Public Opinion',
                    'Media Coverage',
                  ].map((factor) => (
                    <Chip
                      key={factor}
                      label={factor}
                      clickable
                      color={persona.voting.decisionFactors.includes(factor) ? 'primary' : 'default'}
                      onClick={() => handleArrayToggle('voting', 'decisionFactors', factor)}
                      disabled={persona.voting.decisionFactors.length >= 3 && !persona.voting.decisionFactors.includes(factor)}
                    />
                  ))}
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Important Candidate Qualities (Select up to 3)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {[
                    'Leadership',
                    'Integrity',
                    'Experience',
                    'Education',
                    'Communication',
                    'Vision',
                    'Empathy',
                    'Decisiveness',
                  ].map((quality) => (
                    <Chip
                      key={quality}
                      label={quality}
                      clickable
                      color={persona.voting.candidateQualities.includes(quality) ? 'primary' : 'default'}
                      onClick={() => handleArrayToggle('voting', 'candidateQualities', quality)}
                      disabled={persona.voting.candidateQualities.length >= 3 && !persona.voting.candidateQualities.includes(quality)}
                    />
                  ))}
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Voting Method</InputLabel>
                  <Select
                    value={persona.voting.votingMethod}
                    label="Preferred Voting Method"
                    onChange={(e) => updatePersona('voting', 'votingMethod', e.target.value)}
                  >
                    <MenuItem value="in-person">In-person at polling station</MenuItem>
                    <MenuItem value="early-voting">Early voting</MenuItem>
                    <MenuItem value="absentee">Absentee ballot</MenuItem>
                    <MenuItem value="electronic">Electronic voting (if available)</MenuItem>
                    <MenuItem value="mobile">Mobile voting unit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Build Your Voter Persona
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anonymous profile to personalize your experience
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 4 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          sx={{ borderRadius: 2 }}
        >
          Back
        </Button>
        
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleComplete}
            startIcon={<CheckIcon />}
            sx={{ borderRadius: 2 }}
          >
            Complete Persona
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            sx={{ borderRadius: 2 }}
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PersonaBuilder;
